import React, { useState, useEffect } from 'react'
import { PropTypes } from 'prop-types'
import {
  CListGroup,
  CListGroupItem,
  CCard,
  CCardBody,
  CRow,
  CCol,
  CButton,
  CContainer,
  CBadge,
  CCollapse,
} from '@coreui/react'
import {
  cilShieldAlt,
  cilCalendar,
  cilCloudDownload,
  cilCheckCircle,
  cilClock,
  cilInfo,
  cilChevronBottom,
  cilChevronTop,
} from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import axios from 'axios'
import store from 'src/store'

import ClaimsReportingInfo from './ClaimsReporting'

const Message = (props) => {
  const { msgTitle, msgBody } = props
  return (
    <CCard className="border-0 shadow-sm">
      <CCardBody className="p-4 text-center">
        <div className="mb-3">
          <CIcon icon={cilInfo} size="3xl" className="text-primary" />
        </div>
        <h5 className="fw-semibold mb-2">{msgTitle}</h5>
        <p className="text-muted mb-0">{msgBody}</p>
      </CCardBody>
    </CCard>
  )
}

Message.propTypes = {
  msgTitle: PropTypes.string,
  msgBody: PropTypes.string,
}

const PolicyCard = (props) => {
  const { policy } = props
  const [isFetchingDocument, setIsFetchingDocument] = useState(false)
  const [showCoverages, setShowCoverages] = useState(false)
  const [showClaims, setShowClaims] = useState(false)
  // console.log('PolicyCard policy:', policy)

  const fetchPolicyDocument = async () => {
    setIsFetchingDocument(true)
    try {
      const token = store.getState().token
      const baseHost = window.location.host.split('.').slice(1).join('.')

      // console.log('Fetching policy document for policy:', policy)
      const response = await axios.get(
        `https://hub-api.${baseHost}/v1/policies/${policy.referenceNumber}/policy`,
        {
          headers: {
            Authorization: `Bearer ${token.access_token}`,
          },
          responseType: 'blob', // Important for downloading files
        },
      )

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Policy_${policy.number}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error fetching policy document:', error)
      alert('Failed to download policy document. Please try again.')
    } finally {
      setIsFetchingDocument(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Issued':
        return 'success'
      case 'Pending':
        return 'warning'
      case 'Expired':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  if (!policy.number) {
    return (
      <CCard className="border-0 shadow-sm">
        <CCardBody className="p-4 text-center">
          <div className="mb-3">
            <CIcon icon={cilShieldAlt} size="3xl" className="text-muted" />
          </div>
          <h5 className="fw-semibold mb-2">No Policy Found</h5>
          <p className="text-muted mb-0">
            We were unable to find your policy in our records. Please contact your broker for more
            information.
          </p>
        </CCardBody>
      </CCard>
    )
  }

  return (
    <>
      {/* Policy Header Card */}
      <CCard className="border-0 shadow-sm mb-4 policy-card">
        <CCardBody className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="p-3 rounded bg-primary bg-opacity-10">
                  <CIcon icon={cilShieldAlt} size="xl" className="text-primary" />
                </div>
                <div>
                  <h3 className="fw-bold mb-1">{policy.productName}</h3>
                  <p className="text-muted mb-0">Policy Number: {policy.number}</p>
                </div>
              </div>
            </div>
            <CBadge color={getStatusColor(policy.status)} className="px-3 py-2 fs-6">
              {policy.status === 'Issued' ? (
                <>
                  <CIcon icon={cilCheckCircle} className="me-1" />
                  {policy.status}
                </>
              ) : (
                <>
                  <CIcon icon={cilClock} className="me-1" />
                  {policy.status}
                </>
              )}
            </CBadge>
          </div>

          {/* Stats Row */}
          <CRow className="g-3 mb-4">
            <CCol xs={12} md={4}>
              <div className="p-3 rounded bg-light h-100">
                <div className="d-flex align-items-center mb-2">
                  <CIcon icon={cilCalendar} className="text-primary me-2" />
                  <small className="text-muted">Policy Period</small>
                </div>
                <div className="fw-semibold">
                  {new Date(policy.start_date).toLocaleDateString()}
                  <span className="text-muted mx-2">→</span>
                  {new Date(policy.end_date).toLocaleDateString()}
                </div>
              </div>
            </CCol>
            <CCol xs={12} md={4}>
              <div className="p-3 rounded bg-light h-100">
                <small className="text-muted d-block mb-2">Aggregate Limit</small>
                <div className="fw-bold fs-5 text-success">{policy.limit}</div>
              </div>
            </CCol>
            <CCol xs={12} md={4}>
              <div className="p-3 rounded bg-light h-100">
                <small className="text-muted d-block mb-2">Retention</small>
                <div className="fw-bold fs-5">{policy.deductible}</div>
              </div>
            </CCol>
          </CRow>

          {/* Named Insured */}
          <div className="mb-4 p-3 rounded bg-light">
            <small className="text-muted d-block mb-1">Named Insured</small>
            <div className="fw-semibold">{policy.insuredName}</div>
          </div>

          {/* Download Button */}
          <div className="d-flex justify-content-center">
            {policy.status === 'Issued' ? (
              <CButton
                color="primary"
                size="lg"
                onClick={fetchPolicyDocument}
                disabled={isFetchingDocument}
                className="px-4"
              >
                <CIcon icon={cilCloudDownload} className="me-2" />
                {isFetchingDocument ? 'Downloading...' : 'Download Policy Document'}
              </CButton>
            ) : (
              <CButton color="secondary" size="lg" disabled={true} className="px-4">
                <CIcon icon={cilClock} className="me-2" />
                Policy Not Yet Issued
              </CButton>
            )}
          </div>
        </CCardBody>
      </CCard>

      {/* Coverages Section */}
      <CCard className="border-0 shadow-sm mb-4">
        <CCardBody
          className="p-3 cursor-pointer"
          onClick={() => setShowCoverages(!showCoverages)}
          style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--cui-tertiary-bg)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-semibold">Coverage Details</h5>
            <CIcon
              icon={showCoverages ? cilChevronTop : cilChevronBottom}
              size="lg"
              className="text-primary"
            />
          </div>
        </CCardBody>
        <CCollapse visible={showCoverages}>
          <CCardBody className="p-4 pt-0">
            <CRow className="g-4">
              <CCol md={6}>
                <div className="h-100 p-3 rounded border">
                  <div className="d-flex align-items-center mb-3">
                    <div className="p-2 rounded bg-primary bg-opacity-10 me-2">
                      <CIcon icon={cilShieldAlt} className="text-primary" />
                    </div>
                    <h6 className="mb-0 fw-semibold">Base Form</h6>
                  </div>
                  <CListGroup flush>
                    {policy.coverages
                      .filter((c) => c.coverageType === 'UnderlyingLimit')
                      .map((c) => (
                        <CListGroupItem key={c.id} className="border-0 ps-0">
                          <CBadge color="primary" className="me-2">
                            ✓
                          </CBadge>
                          {c.displayName}
                        </CListGroupItem>
                      ))}
                  </CListGroup>
                </div>
              </CCol>
              <CCol md={6}>
                <div className="h-100 p-3 rounded border">
                  <div className="d-flex align-items-center mb-3">
                    <div className="p-2 rounded bg-success bg-opacity-10 me-2">
                      <CIcon icon={cilShieldAlt} className="text-success" />
                    </div>
                    <h6 className="mb-0 fw-semibold">Endorsements</h6>
                  </div>
                  <CListGroup flush>
                    {policy.coverages
                      .filter((c) => c.coverageType === 'AdditionalCoverage')
                      .map((c) => (
                        <CListGroupItem key={c.id} className="border-0 ps-0">
                          <CBadge color="success" className="me-2">
                            ✓
                          </CBadge>
                          {c.displayName}
                        </CListGroupItem>
                      ))}
                  </CListGroup>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCollapse>
      </CCard>

      {/* Claims Reporting Section */}
      <CCard className="border-0 shadow-sm mb-4">
        <CCardBody
          className="p-3 cursor-pointer"
          onClick={() => setShowClaims(!showClaims)}
          style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--cui-tertiary-bg)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-semibold">Claims Reporting Information</h5>
            <CIcon
              icon={showClaims ? cilChevronTop : cilChevronBottom}
              size="lg"
              className="text-primary"
            />
          </div>
        </CCardBody>
        <CCollapse visible={showClaims}>
          <CCardBody className="p-4 pt-0">
            <ClaimsReportingInfo productCode={policy.productCode} />
          </CCardBody>
        </CCollapse>
      </CCard>
    </>
  )
}

PolicyCard.propTypes = {
  policy: PropTypes.object,
  index: PropTypes.number,
  total: PropTypes.number,
}

export const getPolicies = () => {
  const token = store.getState().token
  const policy = store.getState().chainThatPolicy
  const lastPolicyFetchTime = store.getState().chainThatPolicyFetchTime
  const msgTitle = store.getState().chainThatMsgTitle
  const msgBody = store.getState().chainThatMsgBody

  const fetch_timeout = 10000

  if (lastPolicyFetchTime > new Date().getTime() - fetch_timeout) {
    // console.log(`Policy recently fetched.  policy?.length:"${policy?.length}"`)
    // console.log(`    msgTitle:"${msgTitle}"  msgBody:"${msgBody}"`)
    return
  }

  if (!token?.access_token) {
    console.warn("No token, won't fetch policy")
    const new_store_data = {
      chainThatMsgTitle: 'No Access',
      chainThatMsgBody: 'Please login to view policy information',
      chainThatPolicy: null,
      chainThatPolicyFetchTime: null,
    }
    if (
      msgTitle !== new_store_data.chainThatMsgTitle ||
      msgBody !== new_store_data.chainThatMsgBody ||
      policy ||
      lastPolicyFetchTime
    ) {
      store.dispatch({ type: 'set', ...new_store_data })
    }
    return
  }

  const baseHost = window.location.host.split('.').slice(1).join('.')
  axios
    .get(`https://hub-api.${baseHost}/v1/policies/`, {
      headers: {
        Authorization: `Bearer ${token.access_token}`,
      },
    })
    .then((response) => {
      // console.log('got policy', response)
      store.dispatch({
        type: 'set',
        chainThatPolicy: response.data,
        chainThatPolicyFetchTime: new Date().getTime(),
        chainThatMsgTitle: null,
        chainThatMsgBody: null,
      })
    })
    .catch((error) => {
      console.log('Policy.getPolicies -- Error getting policy', error)

      let errorTitle = 'Error Getting Policy'
      let errorBody = 'Something went wrong. Please try again later.'

      if (error.response) {
        // Server responded with error status
        const status = error.response.status
        const data = error.response.data

        switch (status) {
          case 404:
            errorTitle = 'No Policy Found'
            errorBody =
              "No policy information is available for your account. This may be because you haven't been assigned a policy identifier yet. Please contact your broker or administrator for assistance."
            break
          case 403:
            errorTitle = 'Access Denied'
            errorBody =
              "You don't have permission to access policy information. This may be because your account hasn't been properly configured with the necessary identifiers. Please contact your administrator for assistance."
            break
          case 401:
            errorTitle = 'Authentication Required'
            errorBody =
              "Your session has expired or you don't have valid credentials. Please try logging out and logging back in."
            break
          case 500:
          case 502:
          case 503:
            errorTitle = 'Server Error'
            errorBody =
              'The policy service is currently unavailable. Please try again in a few minutes or contact support if the problem persists.'
            break
          default:
            // Check if the error message provides more specific information
            if (data && data.message) {
              if (
                data.message.toLowerCase().includes('identifier') ||
                data.message.toLowerCase().includes('not found') ||
                data.message.toLowerCase().includes('no policy')
              ) {
                errorTitle = 'Policy Not Available'
                errorBody =
                  "No policy information could be found for your account. This typically means you haven't been assigned the necessary identifiers to access your policy. Please contact your broker or administrator."
              } else {
                errorBody = `Error: ${data.message}. Please contact support if this problem continues.`
              }
            }
        }
      } else if (error.request) {
        // Network error
        errorTitle = 'Connection Error'
        errorBody =
          'Unable to connect to the policy service. Please check your internet connection and try again.'
      }

      if (msgTitle !== errorTitle || msgBody !== errorBody || policy || lastPolicyFetchTime) {
        console.debug('  getPolicies new error:', errorTitle, errorBody)
        store.dispatch({
          type: 'set',
          chainThatMsgTitle: errorTitle,
          chainThatMsgBody: errorBody,
          chainThatPolicy: null,
          chainThatPolicyFetchTime: null,
        })
      } else {
        console.error('Duplicate error, avoiding loop')
        console.error(`  msgTitle:"${msgTitle}" vs errorTitle:"${errorTitle}"`)
        console.error(`  msgBody:"${msgBody}" vs errorBody:"${errorBody}"`)
        console.error(`  policy:${policy}`)
        console.error(`  lastPolicyFetchTime:${lastPolicyFetchTime}`)
      }
    })
}

const Policy = () => {
  const [policy, setPolicy] = useState(store.getState().chainThatPolicy)
  const [msgTitle, setMsgTitle] = useState(store.getState().chainThatMsgTitle)
  const [msgBody, setMsgBody] = useState(store.getState().chainThatMsgBody)
  const [token, setToken] = useState(store.getState().token)
  const [selectedPolicyIndex, setSelectedPolicyIndex] = useState(0)
  const [user] = useState(store.getState().user)

  // Subscribe to store updates
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const state = store.getState()
      setPolicy(state.chainThatPolicy)
      setMsgTitle(state.chainThatMsgTitle)
      setMsgBody(state.chainThatMsgBody)
      setToken(state.token)
    })
    return () => unsubscribe()
  }, [])

  // Fetch policies when token changes
  useEffect(() => {
    getPolicies()
  }, [token])

  // Sort policies by end_date (most recent first) and auto-select the most recent one
  useEffect(() => {
    if (policy && policy.length > 0) {
      setSelectedPolicyIndex(0)
    }
  }, [policy])

  // console.debug(`    policy?.length:"${policy?.length}"`)
  // console.debug(`    msgTitle:"${msgTitle}"  msgBody:"${msgBody}"`)

  const getStatusColor = (status) => {
    switch (status) {
      case 'Issued':
        return 'success'
      case 'Pending':
        return 'warning'
      case 'Expired':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  // Sort policies by end date (most recent first)
  let sortedPolicies = policy
    ? [...policy].sort((a, b) => new Date(b.end_date) - new Date(a.end_date))
    : null

  const demoPolicy = {
    id: '11112222-3333-4444-5555-666677778888',
    insuredName: 'Demo Insured',
    referenceNumber: 'SUB-360-00000',
    number: 'POL-360-00000000-00',
    productName: 'Fusion TMK 360',
    productCode: 'TMK1',
    status: 'Pending',
    start_date: '2025-08-08',
    end_date: '2026-08-08',
    policy_link: '',
    application_link: '',
    scan_link: '',
    coverages: [
      {
        displayName: 'Betterments',
        coverageType: 'AdditionalCoverage',
        effectiveDate: '2025-08-08',
        expirationDate: '2026-08-08',
        limit: '100%',
        deductible: null,
      },
      {
        displayName: 'Corporate Impersonation',
        coverageType: 'UnderlyingLimit',
        effectiveDate: '2025-08-08',
        expirationDate: '2026-08-08',
        limit: '$250,000',
        deductible: null,
      },
      {
        displayName: 'Cloud Consumption Fraud',
        coverageType: 'AdditionalCoverage',
        effectiveDate: '2025-08-08',
        expirationDate: '2026-08-08',
        limit: '$50,000',
        deductible: null,
      },
      {
        displayName: 'Policy Aggregate',
        coverageType: 'BasicCoverage',
        effectiveDate: '2025-08-08',
        expirationDate: '2026-08-08',
        limit: '$1,000,000',
        deductible: '$1,000',
      },
      {
        displayName: 'Breach Costs Outside the Limit',
        coverageType: 'UnderlyingLimit',
        effectiveDate: '2025-08-08',
        expirationDate: '2026-08-08',
        limit: '$1,000,000',
        deductible: null,
      },
      {
        displayName: 'Social Engineering',
        coverageType: 'UnderlyingLimit',
        effectiveDate: '2025-08-08',
        expirationDate: '2026-08-08',
        limit: '$250,000',
        deductible: '$1,000',
      },
    ],
    limit: '$1,000,000',
    deductible: '$1,000',
  }
  if (user?.email === 'demo@fusionmga.com') {
    sortedPolicies = [demoPolicy]
  }

  const hasMultiplePolicies = sortedPolicies && sortedPolicies.length > 1

  return (
    <CContainer fluid className="px-4">
      {/* Modern Dashboard Header */}
      <div className="mb-5">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h1 className="display-6 fw-bold mb-2 text-dark policy-title">
              {hasMultiplePolicies ? 'Your Policies' : 'Your Policy'}
            </h1>
            <p className="text-muted fs-5 mb-0">
              Comprehensive cyber insurance coverage details and documentation
            </p>
          </div>
        </div>
      </div>

      {/* Policy Selector (only show if multiple policies) */}
      {hasMultiplePolicies && (
        <div className="mb-4">
          <h5 className="fw-semibold mb-3">Select a Policy</h5>
          <CRow className="g-3">
            {sortedPolicies.map((p, index) => (
              <CCol key={p.id} xs={12} md={6} lg={4}>
                <CCard
                  className={`border-0 shadow-sm policy-selector-card ${
                    selectedPolicyIndex === index ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedPolicyIndex(index)}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border:
                      selectedPolicyIndex === index
                        ? '2px solid var(--cui-primary)'
                        : '2px solid transparent',
                  }}
                >
                  <CCardBody className="p-3">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                      <div className="flex-grow-1">
                        <h6 className="fw-semibold mb-1">{p.productName}</h6>
                        <small className="text-muted">{p.number}</small>
                      </div>
                      <CBadge color={getStatusColor(p.status)} className="ms-2">
                        {p.status}
                      </CBadge>
                    </div>
                    <div className="mt-2 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <CIcon icon={cilCalendar} size="sm" className="me-1" />
                          {new Date(p.start_date).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric',
                          })}{' '}
                          -{' '}
                          {new Date(p.end_date).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric',
                          })}
                        </small>
                        {selectedPolicyIndex === index && (
                          <CIcon icon={cilCheckCircle} className="text-primary" />
                        )}
                      </div>
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>
            ))}
          </CRow>
        </div>
      )}

      {/* Policy Content */}
      {msgTitle && msgBody && <Message msgTitle={msgTitle} msgBody={msgBody} />}
      {sortedPolicies ? (
        sortedPolicies.length < 1 ? (
          <Message msgTitle="No Policy Found" msgBody="Please contact your broker." />
        ) : (
          <PolicyCard
            policy={sortedPolicies[selectedPolicyIndex]}
            index={selectedPolicyIndex}
            total={sortedPolicies.length}
            key={sortedPolicies[selectedPolicyIndex].id}
          />
        )
      ) : !msgTitle || !msgBody ? (
        <Message msgTitle={'Fetching Policy Data...'} msgBody={'...'} />
      ) : (
        ''
      )}

      {/* Custom Styles */}
      <style>{`
        .policy-card {
          transition: all 0.3s ease;
        }

        .policy-card:hover {
          box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
        }

        .policy-selector-card {
          transition: all 0.3s ease;
        }

        .policy-selector-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.1) !important;
        }

        .policy-selector-card.selected {
          background: linear-gradient(135deg, rgba(var(--cui-primary-rgb), 0.05) 0%, rgba(var(--cui-info-rgb), 0.05) 100%);
        }

        .policy-title {
          background: linear-gradient(135deg, var(--cui-primary) 0%, var(--cui-info) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
    </CContainer>
  )
}

export default Policy
